import { useState, useEffect, useMemo } from 'react'
import { supabase } from '../lib/supabase'
import { colors, formatNumber } from '../styles/tokens'
import { Button, Input, Select } from './FormControls'
import { calculateRebarColumn, MODULE_REGISTRY } from '../lib/quantityEngine'
import { useDraftPersistence } from '../lib/useDraftPersistence'

// =======================================================================
// RebarColumnModulePanel — the ONE Rebar Column engineering calculation
// module (FINAL LOCKED Contract).
//
// GLOBAL count-based Output semantic (LOCKED, applies to any future
// count-based module too, not just Column):
//   Count = number of IDENTICAL instances represented by ONE selected
//   count-based takeoff_item. The engineering configuration entered
//   here describes ONE typical instance; calculateRebarColumn() itself
//   calculates that single instance ONLY — it has NO knowledge of count
//   multiplication, which happens strictly above it, at this
//   component's Output-aggregation boundary. Different configurations
//   (e.g. C1 vs C2) MUST be separate takeoff_items and separate
//   Outputs — this is therefore a Single-select, not Multi-select.
// =======================================================================

const BAR_DIAMETERS = [8, 10, 12, 16, 20, 25, 32, 40]

export default function RebarColumnModulePanel({ scopeId, boqItemId, linkedItems, superseding, onClose, onError, saving, onSave }) {
  const draftKey = `draft:rebar_column:${scopeId || boqItemId || 'unknown'}`
  const [draft, setDraft, clearDraft] = useDraftPersistence(draftKey, {
    selectedColumnId: null,
    columnConfig: {}, // { shape, height, width, depth, diameter, cover, transverseType }
    longitudinalGroups: [], tieZones: [], spiralZones: [], lapPct: '', wastePct: '',
  })

  const initial = superseding
    ? {
        selectedColumnId: superseding.geometry_snapshot?.items?.[0]?.takeoff_item_id || null,
        columnConfig: superseding.engineering_inputs?.columnConfig || {},
        longitudinalGroups: superseding.engineering_inputs?.longitudinalGroups || [],
        tieZones: superseding.engineering_inputs?.tieZones || [],
        spiralZones: superseding.engineering_inputs?.spiralZones || [],
        lapPct: superseding.engineering_inputs?.lapPct ?? '', wastePct: superseding.engineering_inputs?.wastePct ?? '',
      }
    : draft

  const [selectedColumnId, setSelectedColumnIdRaw] = useState(initial.selectedColumnId)
  const [resolvedColumn, setResolvedColumn] = useState(null)
  const [columnConfig, setColumnConfigRaw] = useState(initial.columnConfig)
  const [longitudinalGroups, setLongitudinalGroupsRaw] = useState(initial.longitudinalGroups)
  const [tieZones, setTieZonesRaw] = useState(initial.tieZones)
  const [spiralZones, setSpiralZonesRaw] = useState(initial.spiralZones)
  const [lapPct, setLapPctRaw] = useState(initial.lapPct)
  const [wastePct, setWastePctRaw] = useState(initial.wastePct)
  const [selectedBasis, setSelectedBasis] = useState('procurement')

  const setSelectedColumnId = (id) => { setSelectedColumnIdRaw(id); if (!superseding) setDraft((d) => ({ ...d, selectedColumnId: id })) }
  const setColumnConfig = (updater) => setColumnConfigRaw((prev) => {
    const next = typeof updater === 'function' ? updater(prev) : updater
    if (!superseding) setDraft((d) => ({ ...d, columnConfig: next }))
    return next
  })
  const mk = (setter, key) => (updater) => setter((prev) => {
    const next = typeof updater === 'function' ? updater(prev) : updater
    if (!superseding) setDraft((d) => ({ ...d, [key]: next }))
    return next
  })
  const setLongitudinalGroups = mk(setLongitudinalGroupsRaw, 'longitudinalGroups')
  const setTieZones = mk(setTieZonesRaw, 'tieZones')
  const setSpiralZones = mk(setSpiralZonesRaw, 'spiralZones')
  const setLapPct = (v) => { setLapPctRaw(v); if (!superseding) setDraft((d) => ({ ...d, lapPct: v })) }
  const setWastePct = (v) => { setWastePctRaw(v); if (!superseding) setDraft((d) => ({ ...d, wastePct: v })) }

  // The live count is ALWAYS re-fetched from the takeoff item on every
  // render of a selected column — Recalculate therefore naturally picks
  // up the CURRENT count (e.g. 7), never silently reverting to a stale
  // or hard-coded value. The historical Breakdown for an unmodified,
  // already-saved Output instead reads its OWN frozen
  // geometry_snapshot.items[0].value (handled by OutputRow elsewhere,
  // unrelated to this live-editing component).
  useEffect(() => {
    if (!selectedColumnId) { setResolvedColumn(null); return }
    ;(async () => {
      const item = linkedItems.find((r) => r.id === selectedColumnId)
      const { data, error } = await supabase.from('takeoff_item_aggregated_quantity').select('aggregated_quantity').eq('takeoff_item_id', selectedColumnId).maybeSingle()
      if (error) { onError(error.message); return }
      setResolvedColumn({ label: item?.description || selectedColumnId, geometryCount: data?.aggregated_quantity ?? 0 })
    })()
  }, [selectedColumnId, linkedItems, onError])

  function updateColumnConfig(patch) { setColumnConfig((prev) => ({ ...prev, ...patch })) }
  function addLongitudinalGroup() { setLongitudinalGroups((prev) => [...prev, { id: `LG${Date.now()}`, label: '', diameter: '', numberOfBars: '', lapAllowanceLength: '', developmentLengthOverride: '', explicitBarLength: '' }]) }
  function updateLongitudinalGroup(id, patch) { setLongitudinalGroups((prev) => prev.map((g) => (g.id === id ? { ...g, ...patch } : g))) }
  function removeLongitudinalGroup(id) { setLongitudinalGroups((prev) => prev.filter((g) => g.id !== id)) }
  function addTieZone() { setTieZones((prev) => [...prev, { id: `TZ${Date.now()}`, label: '', startOffset: '', endOffset: '', diameter: '', spacing: '', legs: 2, numberOfHooksPerTie: 2, hookLengthPerHook: '' }]) }
  function updateTieZone(id, patch) { setTieZones((prev) => prev.map((z) => (z.id === id ? { ...z, ...patch } : z))) }
  function removeTieZone(id) { setTieZones((prev) => prev.filter((z) => z.id !== id)) }
  function addSpiralZone() { setSpiralZones((prev) => [...prev, { id: `SZ${Date.now()}`, label: '', startOffset: '', endOffset: '', diameter: '', pitch: '' }]) }
  function updateSpiralZone(id, patch) { setSpiralZones((prev) => prev.map((z) => (z.id === id ? { ...z, ...patch } : z))) }
  function removeSpiralZone(id) { setSpiralZones((prev) => prev.filter((z) => z.id !== id)) }

  // ONE typical column geometry — never multiple. `height`/`cover`/etc.
  // describe the single representative instance the estimator designed.
  const typicalColumn = useMemo(() => {
    if (!resolvedColumn) return null
    return {
      label: resolvedColumn.label,
      shape: columnConfig.shape || 'rectangular',
      height: columnConfig.height === '' || columnConfig.height == null ? NaN : Number(columnConfig.height),
      width: columnConfig.width === '' || columnConfig.width == null ? NaN : Number(columnConfig.width),
      depth: columnConfig.depth === '' || columnConfig.depth == null ? NaN : Number(columnConfig.depth),
      diameter: columnConfig.diameter === '' || columnConfig.diameter == null ? NaN : Number(columnConfig.diameter),
      cover: columnConfig.cover === '' || columnConfig.cover == null ? NaN : Number(columnConfig.cover),
      transverseType: columnConfig.transverseType || 'ties',
    }
  }, [resolvedColumn, columnConfig])

  const anyExplicitLapInDraft = longitudinalGroups.some((g) => Number(g.lapAllowanceLength || 0) > 0)

  const longitudinalForCalc = useMemo(() => longitudinalGroups.map((g) => ({
    ...g, diameter: g.diameter === '' ? NaN : Number(g.diameter), numberOfBars: g.numberOfBars === '' ? NaN : Number(g.numberOfBars),
    lapAllowanceLength: g.lapAllowanceLength === '' ? 0 : Number(g.lapAllowanceLength),
    developmentLengthOverride: g.developmentLengthOverride === '' ? 0 : Number(g.developmentLengthOverride),
    explicitBarLength: g.explicitBarLength === '' ? undefined : Number(g.explicitBarLength),
  })), [longitudinalGroups])

  const tieZonesForCalc = useMemo(() => tieZones.map((z) => ({
    ...z, startOffset: z.startOffset === '' ? NaN : Number(z.startOffset),
    endOffset: z.useColumnHeightAsEnd ? (typicalColumn?.height ?? NaN) : (z.endOffset === '' ? NaN : Number(z.endOffset)),
    diameter: z.diameter === '' ? NaN : Number(z.diameter), spacing: z.spacing === '' ? NaN : Number(z.spacing),
    legs: Number(z.legs), numberOfHooksPerTie: Number(z.numberOfHooksPerTie), hookLengthPerHook: z.hookLengthPerHook === '' ? NaN : Number(z.hookLengthPerHook),
  })), [tieZones, typicalColumn])

  const spiralZonesForCalc = useMemo(() => spiralZones.map((z) => ({
    ...z, startOffset: z.startOffset === '' ? NaN : Number(z.startOffset),
    endOffset: z.useColumnHeightAsEnd ? (typicalColumn?.height ?? NaN) : (z.endOffset === '' ? NaN : Number(z.endOffset)),
    diameter: z.diameter === '' ? NaN : Number(z.diameter), pitch: z.pitch === '' ? NaN : Number(z.pitch),
  })), [spiralZones, typicalColumn])

  // --- Per-instance engineering result (calculateRebarColumn UNCHANGED,
  //     LOCKED formulas — always exactly ONE column, count is invisible
  //     to it) ---
  const perInstanceResult = useMemo(() => {
    if (!typicalColumn) return { value: null, error: null }
    try {
      return { value: calculateRebarColumn([typicalColumn], longitudinalForCalc, tieZonesForCalc, spiralZonesForCalc, {
        lapPct: lapPct === '' ? NaN : Number(lapPct), wastePct: wastePct === '' ? NaN : Number(wastePct),
      }), error: null }
    } catch (e) {
      return { value: null, error: e.message }
    }
  }, [typicalColumn, longitudinalForCalc, tieZonesForCalc, spiralZonesForCalc, lapPct, wastePct])

  // --- Count multiplication happens HERE, strictly above the locked
  //     engineering calculation, at the Output-aggregation boundary ---
  const geometryCount = resolvedColumn?.geometryCount ?? 0
  const result = useMemo(() => {
    if (!perInstanceResult.value) return perInstanceResult
    if (!(geometryCount > 0)) return { value: null, error: 'The selected takeoff item has a Count of 0 — at least 1 identical column instance is required.' }
    const per = perInstanceResult.value
    const scale = (v) => v * geometryCount
    return {
      value: {
        perInstance: per,
        geometryCount,
        columnBreakdown: per.columnBreakdown, // single instance, shown once in the UI
        steps: per.steps.map((s) => ({ ...s, value: scale(s.value) })),
        quantityBases: {
          theoretical: { label: per.quantityBases.theoretical.label, valueKg: scale(per.quantityBases.theoretical.valueKg), valueTon: scale(per.quantityBases.theoretical.valueTon) },
          theoreticalPlusLap: { label: per.quantityBases.theoreticalPlusLap.label, valueKg: scale(per.quantityBases.theoreticalPlusLap.valueKg), valueTon: scale(per.quantityBases.theoreticalPlusLap.valueTon) },
          procurement: { label: per.quantityBases.procurement.label, valueKg: scale(per.quantityBases.procurement.valueKg), valueTon: scale(per.quantityBases.procurement.valueTon) },
        },
      },
      error: null,
    }
  }, [perInstanceResult, geometryCount])

  async function handleSave() {
    if (!result.value) return
    const chosen = result.value.quantityBases[selectedBasis]

    // The count is FROZEN into geometry_snapshot at save time — never
    // hard-coded to 1. This is what the general Geometry Changed
    // mechanism compares against the live count on every future view.
    const geometrySnapshot = {
      items: [{
        takeoff_item_id: selectedColumnId, measurement_type: 'count', value: geometryCount, unit: 'no', label: resolvedColumn.label,
        detail: { shape: typicalColumn.shape, height: typicalColumn.height, width: typicalColumn.width, depth: typicalColumn.depth, diameter: typicalColumn.diameter, cover: typicalColumn.cover, transverseType: typicalColumn.transverseType },
      }],
    }
    const engineeringInputs = { columnConfig, longitudinalGroups, tieZones, spiralZones, lapPct: Number(lapPct), wastePct: Number(wastePct), quantityBasisChosen: selectedBasis }

    const succeeded = await onSave({
      calculatedValueTon: chosen.valueTon, unit: 'ton', geometrySnapshot, engineeringInputs,
      breakdownSnapshot: { perInstance: result.value.perInstance, geometryCount, steps: result.value.steps, columnBreakdown: result.value.columnBreakdown, quantityBases: result.value.quantityBases },
      takeoffItemIdForProvenance: selectedColumnId,
    })
    if (succeeded !== false) clearDraft()
  }

  const hasUnresolvedConfig = !typicalColumn || Number.isNaN(typicalColumn.height) || Number.isNaN(typicalColumn.cover) ||
    (typicalColumn.shape === 'circular' ? Number.isNaN(typicalColumn.diameter) : (Number.isNaN(typicalColumn.width) || Number.isNaN(typicalColumn.depth)))

  return (
    <div>
      <div style={{ padding: '8px 10px', background: '#FFF8E1', border: `1px solid #F5C542`, borderRadius: 6, fontSize: 11, color: '#7A5C00', marginBottom: 14 }}>
        ⚠ Select ONE count-based takeoff item — its Count is the number of IDENTICAL columns this configuration represents. If different columns have different reinforcement, create a separate takeoff item and a separate Output for each configuration. "Number of Bars" below is the count YOU determine around one column's perimeter.
      </div>

      <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: colors.textSecondary, textTransform: 'uppercase', marginBottom: 5 }}>Select Column Configuration (one takeoff item)</label>
      <Select value={selectedColumnId || ''} onChange={(e) => setSelectedColumnId(e.target.value || null)} style={{ width: '100%', marginBottom: 10 }}>
        <option value="">— Select —</option>
        {linkedItems.filter((it) => it.measurement_type === 'count').map((item) => <option key={item.id} value={item.id}>{item.description}</option>)}
      </Select>
      {resolvedColumn && (
        <div style={{ fontSize: 12, color: colors.textSecondary, marginBottom: 10 }}>
          Quantity: <strong>{formatNumber(resolvedColumn.geometryCount)} No.</strong> identical column(s)
        </div>
      )}

      {typicalColumn && (
        <div style={{ border: `1px dashed ${colors.border}`, borderRadius: 6, padding: 8, marginBottom: 8 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(90px, 1fr))', gap: 6, marginBottom: 6 }}>
            <LabeledSelect label="Shape" value={columnConfig.shape || 'rectangular'} onChange={(e) => updateColumnConfig({ shape: e.target.value })}>
              <option value="rectangular">Rectangular / Square</option>
              <option value="circular">Circular</option>
            </LabeledSelect>
            <LabeledInput label="Height (m)" type="number" value={columnConfig.height ?? ''} onChange={(e) => updateColumnConfig({ height: e.target.value })} />
            <LabeledInput label="Cover (mm)" type="number" value={columnConfig.cover ?? ''} onChange={(e) => updateColumnConfig({ cover: e.target.value })} />
            <LabeledSelect label="Transverse Type" value={columnConfig.transverseType || 'ties'} onChange={(e) => updateColumnConfig({ transverseType: e.target.value })}>
              <option value="ties">Ties</option>
              <option value="spiral">Spiral</option>
            </LabeledSelect>
          </div>
          {(columnConfig.shape || 'rectangular') === 'circular' ? (
            <LabeledInput label="Diameter (m)" type="number" value={columnConfig.diameter ?? ''} onChange={(e) => updateColumnConfig({ diameter: e.target.value })} />
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
              <LabeledInput label="Width (m)" type="number" value={columnConfig.width ?? ''} onChange={(e) => updateColumnConfig({ width: e.target.value })} />
              <LabeledInput label="Depth (m)" type="number" value={columnConfig.depth ?? ''} onChange={(e) => updateColumnConfig({ depth: e.target.value })} />
            </div>
          )}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <label style={{ fontSize: 11, fontWeight: 700, color: colors.textSecondary, textTransform: 'uppercase' }}>Longitudinal Reinforcement (per column)</label>
        <Button size="sm" variant="secondary" onClick={addLongitudinalGroup}>+ Add Group</Button>
      </div>
      {longitudinalGroups.map((g) => (
        <div key={g.id} style={{ border: `1px solid ${colors.border}`, borderRadius: 6, padding: 8, marginBottom: 6 }}>
          <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
            <Input placeholder="Group name (e.g. Main Bars)" value={g.label} onChange={(e) => updateLongitudinalGroup(g.id, { label: e.target.value })} style={{ flex: 1, fontSize: 11 }} />
            <button onClick={() => removeLongitudinalGroup(g.id)} style={{ background: 'none', border: 'none', color: colors.danger, cursor: 'pointer' }}>✕</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(90px, 1fr))', gap: 6, marginBottom: 6 }}>
            <LabeledSelect label="Diameter" value={g.diameter} onChange={(e) => updateLongitudinalGroup(g.id, { diameter: e.target.value })}>
              <option value="" disabled>Ø (mm)</option>
              {BAR_DIAMETERS.map((d) => <option key={d} value={d}>Ø{d}</option>)}
            </LabeledSelect>
            <LabeledInput label="No. of Bars (around perimeter)" type="number" value={g.numberOfBars} onChange={(e) => updateLongitudinalGroup(g.id, { numberOfBars: e.target.value })} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
            <LabeledInput label="Lap Allowance Length (mm, optional)" type="number" value={g.lapAllowanceLength} onChange={(e) => updateLongitudinalGroup(g.id, { lapAllowanceLength: e.target.value })} />
            <LabeledInput label="Development Length Override (mm, optional)" type="number" value={g.developmentLengthOverride} onChange={(e) => updateLongitudinalGroup(g.id, { developmentLengthOverride: e.target.value })} />
            <LabeledInput label="Explicit Bar Length (m, optional)" type="number" value={g.explicitBarLength} onChange={(e) => updateLongitudinalGroup(g.id, { explicitBarLength: e.target.value })} />
          </div>
        </div>
      ))}
      {anyExplicitLapInDraft && (
        <div style={{ fontSize: 11, color: colors.warningText, background: colors.warningBg, borderRadius: 6, padding: '6px 10px', marginBottom: 10 }}>
          ℹ A Lap Allowance Length is set on at least one group — the general Lap/Splice % below is disabled for this Output to avoid double-counting.
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '14px 0 8px' }}>
        <label style={{ fontSize: 11, fontWeight: 700, color: colors.textSecondary, textTransform: 'uppercase' }}>Tie Zones (per column)</label>
        <Button size="sm" variant="secondary" onClick={addTieZone}>+ Add Tie Zone</Button>
      </div>
      {tieZones.map((z, zIndex) => {
        const isLastZone = zIndex === tieZones.length - 1
        return (
        <div key={z.id} style={{ border: `1px solid ${colors.border}`, borderRadius: 6, padding: 8, marginBottom: 6 }}>
          <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
            <Input placeholder="Zone name (e.g. Bottom Confinement)" value={z.label} onChange={(e) => updateTieZone(z.id, { label: e.target.value })} style={{ flex: 1, fontSize: 11 }} />
            <button onClick={() => removeTieZone(z.id)} style={{ background: 'none', border: 'none', color: colors.danger, cursor: 'pointer' }}>✕</button>
          </div>
          <LabeledInput label="Start Offset (m)" type="number" value={z.startOffset} onChange={(e) => updateTieZone(z.id, { startOffset: e.target.value })} />
          <div style={{ margin: '6px 0' }}>
            <label style={{ display: 'block', fontSize: 10, color: colors.textMuted, marginBottom: 2 }}>End Offset (m)</label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <Input type="number" value={z.useColumnHeightAsEnd ? '' : z.endOffset} placeholder={z.useColumnHeightAsEnd ? `${typicalColumn?.height != null ? typicalColumn.height.toFixed(2) : ''} m (column height)` : ''} disabled={z.useColumnHeightAsEnd} onChange={(e) => updateTieZone(z.id, { endOffset: e.target.value })} style={{ fontSize: 11, flex: 1, minWidth: 0 }} />
              {isLastZone && (
                <button onClick={() => updateTieZone(z.id, { useColumnHeightAsEnd: !z.useColumnHeightAsEnd, endOffset: '' })} style={{ fontSize: 10, padding: '6px 10px', whiteSpace: 'nowrap', borderRadius: 4, cursor: 'pointer', border: `1px solid ${z.useColumnHeightAsEnd ? colors.primary : colors.border}`, background: z.useColumnHeightAsEnd ? colors.primary : '#fff', color: z.useColumnHeightAsEnd ? '#fff' : colors.textSecondary }}>
                  {z.useColumnHeightAsEnd ? '✓ Using Column Height' : 'Use Column Height'}
                </button>
              )}
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(85px, 1fr))', gap: 6, marginBottom: 6 }}>
            <LabeledSelect label="Diameter" value={z.diameter} onChange={(e) => updateTieZone(z.id, { diameter: e.target.value })}>
              <option value="" disabled>Ø (mm)</option>
              {BAR_DIAMETERS.map((d) => <option key={d} value={d}>Ø{d}</option>)}
            </LabeledSelect>
            <LabeledInput label="Spacing (mm)" type="number" value={z.spacing} onChange={(e) => updateTieZone(z.id, { spacing: e.target.value })} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
            <LabeledSelect label="Legs" value={z.legs} onChange={(e) => updateTieZone(z.id, { legs: e.target.value })}>
              <option value={2}>2-Leg</option><option value={4}>4-Leg</option><option value={6}>6-Leg</option>
            </LabeledSelect>
            <LabeledInput label="Hooks per Tie" type="number" value={z.numberOfHooksPerTie} onChange={(e) => updateTieZone(z.id, { numberOfHooksPerTie: e.target.value })} />
            <LabeledInput label="Hook Length (mm)" type="number" value={z.hookLengthPerHook} onChange={(e) => updateTieZone(z.id, { hookLengthPerHook: e.target.value })} />
          </div>
        </div>
      )})}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '14px 0 8px' }}>
        <label style={{ fontSize: 11, fontWeight: 700, color: colors.textSecondary, textTransform: 'uppercase' }}>Spiral Zones (per column)</label>
        <Button size="sm" variant="secondary" onClick={addSpiralZone}>+ Add Spiral Zone</Button>
      </div>
      {spiralZones.map((z, zIndex) => {
        const isLastZone = zIndex === spiralZones.length - 1
        return (
        <div key={z.id} style={{ border: `1px solid ${colors.border}`, borderRadius: 6, padding: 8, marginBottom: 6 }}>
          <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
            <Input placeholder="Zone name" value={z.label} onChange={(e) => updateSpiralZone(z.id, { label: e.target.value })} style={{ flex: 1, fontSize: 11 }} />
            <button onClick={() => removeSpiralZone(z.id)} style={{ background: 'none', border: 'none', color: colors.danger, cursor: 'pointer' }}>✕</button>
          </div>
          <LabeledInput label="Start Offset (m)" type="number" value={z.startOffset} onChange={(e) => updateSpiralZone(z.id, { startOffset: e.target.value })} />
          <div style={{ margin: '6px 0' }}>
            <label style={{ display: 'block', fontSize: 10, color: colors.textMuted, marginBottom: 2 }}>End Offset (m)</label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <Input type="number" value={z.useColumnHeightAsEnd ? '' : z.endOffset} placeholder={z.useColumnHeightAsEnd ? `${typicalColumn?.height != null ? typicalColumn.height.toFixed(2) : ''} m (column height)` : ''} disabled={z.useColumnHeightAsEnd} onChange={(e) => updateSpiralZone(z.id, { endOffset: e.target.value })} style={{ fontSize: 11, flex: 1, minWidth: 0 }} />
              {isLastZone && (
                <button onClick={() => updateSpiralZone(z.id, { useColumnHeightAsEnd: !z.useColumnHeightAsEnd, endOffset: '' })} style={{ fontSize: 10, padding: '6px 10px', whiteSpace: 'nowrap', borderRadius: 4, cursor: 'pointer', border: `1px solid ${z.useColumnHeightAsEnd ? colors.primary : colors.border}`, background: z.useColumnHeightAsEnd ? colors.primary : '#fff', color: z.useColumnHeightAsEnd ? '#fff' : colors.textSecondary }}>
                  {z.useColumnHeightAsEnd ? '✓ Using Column Height' : 'Use Column Height'}
                </button>
              )}
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 6 }}>
            <LabeledSelect label="Diameter" value={z.diameter} onChange={(e) => updateSpiralZone(z.id, { diameter: e.target.value })}>
              <option value="" disabled>Ø (mm)</option>
              {BAR_DIAMETERS.map((d) => <option key={d} value={d}>Ø{d}</option>)}
            </LabeledSelect>
            <LabeledInput label="Pitch (mm)" type="number" value={z.pitch} onChange={(e) => updateSpiralZone(z.id, { pitch: e.target.value })} />
          </div>
        </div>
      )})}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 10, marginBottom: 14 }}>
        <div>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: colors.textSecondary, marginBottom: 5 }}>Lap/Splice % {anyExplicitLapInDraft ? '(disabled — using explicit Lap Allowance Length)' : '— required'}</label>
          <Input type="number" value={anyExplicitLapInDraft ? '0' : lapPct} disabled={anyExplicitLapInDraft} onChange={(e) => setLapPct(e.target.value)} style={{ width: '100%' }} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: colors.textSecondary, marginBottom: 5 }}>Waste % — required</label>
          <Input type="number" value={wastePct} onChange={(e) => setWastePct(e.target.value)} style={{ width: '100%' }} />
        </div>
      </div>

      {result.value && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ padding: '8px 10px', background: '#F0F4FF', borderRadius: 6, marginBottom: 8, fontSize: 12, fontWeight: 700, color: colors.textPrimary }}>
            Quantity: {formatNumber(result.value.geometryCount)} No.
          </div>
          {result.value.columnBreakdown.map((cb, ci) => (
            <div key={ci} style={{ border: `1px solid ${colors.border}`, borderRadius: 8, padding: 10, marginBottom: 8, background: '#FAFBFC' }}>
              <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>{cb.column.label} — {cb.column.shape}, H={formatNumber(cb.column.height)}m (per typical column)</div>
              {cb.longitudinalResults.map((lr, li) => (
                <div key={'l' + li} style={{ fontSize: 11, color: colors.textSecondary, padding: '3px 0' }}>
                  <strong>{lr.group.label || 'Group'}</strong> (Ø{lr.group.diameter} × {lr.group.numberOfBars}): Length {formatNumber(lr.barLength)}m → <strong>{formatNumber(lr.grossWeight)} kg</strong>
                </div>
              ))}
              {cb.transverseResults.map((tr, ti) => (
                <div key={'t' + ti} style={{ fontSize: 11, color: colors.textSecondary, padding: '3px 0' }}>
                  <strong>{tr.zone.label}</strong> ({tr.kind === 'tie' ? `${tr.numberOfTies} ties, Ø${tr.zone.diameter}@${tr.zone.spacing}mm` : `${tr.numberOfTurns.toFixed(1)} turns, Ø${tr.zone.diameter}@${tr.zone.pitch}mm pitch`}) → <strong>{formatNumber(tr.grossWeight)} kg</strong>
                </div>
              ))}
              <div style={{ marginTop: 6, paddingTop: 6, borderTop: `1px solid ${colors.border}`, fontSize: 12, fontWeight: 700 }}>Weight / Column: {formatNumber(cb.columnGross)} kg</div>
            </div>
          ))}
          <div style={{ padding: '8px 10px', background: '#FFFBEB', borderRadius: 6, marginBottom: 8, fontSize: 12, fontWeight: 700, display: 'flex', justifyContent: 'space-between' }}>
            <span>Total Theoretical Weight = {formatNumber(result.value.perInstance.columnBreakdown[0].columnGross)} kg × {formatNumber(result.value.geometryCount)}</span>
            <span>{formatNumber(result.value.quantityBases.theoretical.valueKg)} kg</span>
          </div>
          <div style={{ padding: '10px 12px', background: '#F8FAFF', border: `1px solid ${colors.primary}`, borderRadius: 8 }}>
            {result.value.steps.map((s, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '3px 0' }}>
                <span style={{ color: colors.textSecondary }}>{s.label}</span><span style={{ fontWeight: 600 }}>{formatNumber(s.value)} {s.unit}</span>
              </div>
            ))}
          </div>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: colors.textSecondary, marginTop: 12, marginBottom: 5 }}>Save as Resource Quantity Output — choose basis</label>
          <Select value={selectedBasis} onChange={(e) => setSelectedBasis(e.target.value)} style={{ width: '100%' }}>
            <option value="theoretical">{result.value.quantityBases.theoretical.label} — {formatNumber(result.value.quantityBases.theoretical.valueTon)} ton</option>
            <option value="theoreticalPlusLap">{result.value.quantityBases.theoreticalPlusLap.label} — {formatNumber(result.value.quantityBases.theoreticalPlusLap.valueTon)} ton</option>
            <option value="procurement">{result.value.quantityBases.procurement.label} — {formatNumber(result.value.quantityBases.procurement.valueTon)} ton</option>
          </Select>
        </div>
      )}
      {result.error && <div style={{ fontSize: 12, color: colors.textMuted, marginBottom: 14, fontStyle: 'italic' }}>{result.error}</div>}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {!superseding && (
          <button onClick={() => { if (window.confirm('Discard all unsaved input for this module? This cannot be undone.')) { clearDraft(); onClose() } }} style={{ background: 'none', border: 'none', color: colors.danger, cursor: 'pointer', fontSize: 12 }}>
            Discard Draft
          </button>
        )}
        <Button onClick={handleSave} disabled={!result.value || saving || hasUnresolvedConfig} style={{ marginLeft: 'auto' }}>
          {saving ? 'Saving…' : superseding ? 'Save New Version' : 'Save Output'}
        </Button>
      </div>
    </div>
  )
}

function LabeledInput({ label, ...inputProps }) {
  return (
    <div>
      <label style={{ display: 'block', fontSize: 10, color: colors.textMuted, marginBottom: 2 }}>{label}</label>
      <Input {...inputProps} style={{ fontSize: 11, width: '100%' }} />
    </div>
  )
}
function LabeledSelect({ label, children, ...selectProps }) {
  return (
    <div>
      <label style={{ display: 'block', fontSize: 10, color: colors.textMuted, marginBottom: 2 }}>{label}</label>
      <Select {...selectProps} style={{ fontSize: 11, width: '100%' }}>{children}</Select>
    </div>
  )
}
